(() => {
  if (window.TDFWBikeHeightFinder) return;
  window.TDFWBikeHeightFinder = true;
  const states = new WeakMap();
  const rangeRequests = new Map();
  const heightKey = 'bike_height';
  function parseRange(label) {
    const clean = String(label).normalize('NFKC').replace(/\u00a0/g, ' ').replace(/cm/gi, '').trim();
    const match = clean.match(/^(\d{2,3})\s*[-–—]\s*(\d{2,3})$/);
    const open = clean.match(/^(\d{2,3})\s*\+$/);
    if (!match && !open) return null;
    const min = Number((match || open)[1]), max = match ? Number(match[2]) : 250;
    return min >= 50 && max <= 250 && min <= max ? { min, max } : null;
  }
  function parseOptions(template) {
    return Array.from(template.content.querySelectorAll('option')).flatMap(option => {
      const range = parseRange(option.dataset.heightLabel);
      return range ? [{ ...range, value: option.value }] : [];
    });
  }
  function readHeight(value) {
    return /^\d{2,3}$/.test(String(value)) && Number(value) >= 50 && Number(value) <= 250 ? Number(value) : null;
  }
  function error(root, message, invalidInput = false) {
    const output = root.querySelector('[data-height-error]');
    output.textContent = message;
    output.hidden = !message;
    root.querySelector('[data-height-input]').setAttribute('aria-invalid', invalidInput ? 'true' : 'false');
  }
  function init(root) {
    if (states.has(root)) return states.get(root);
    const categories = new Map();
    root.querySelectorAll('[data-height-ranges]').forEach(template => {
      let url;
      try { url = template.dataset.heightUrl ? new URL(template.dataset.heightUrl, location.origin) : null; } catch (_) { url = null; }
      categories.set(template.dataset.heightRanges, { url, ranges: parseOptions(template), loaded: false });
    });
    const state = { categories, parameter: root.dataset.heightFilter, submitting: false };
    states.set(root, state);
    return state;
  }
  function selectedCategory(root, state) {
    return state.categories.get(root.querySelector('[data-height-type]:checked')?.value || 'all');
  }
  function validURL(category) {
    return category?.url?.origin === location.origin && category.url.pathname.includes('/collections/');
  }
  function refreshType(root) {
    const state = init(root), category = selectedCategory(root, state);
    const browse = root.querySelector('[data-height-browse]');
    const unavailable = !validURL(category) || (category.loaded && !category.ranges.length);
    root.querySelector('[data-height-submit]').disabled = unavailable || state.submitting;
    if (validURL(category)) browse.href = category.url.href;
    else {
      const fallback = state.categories.get('all');
      if (validURL(fallback)) browse.href = fallback.url.href;
    }
    browse.hidden = !unavailable;
    error(root, unavailable ? 'De lengtezoeker is voor dit type fiets tijdelijk niet beschikbaar. Kies een ander type of bekijk alle fietsen.' : '');
  }
  const samePath = (a, b) => a.replace(/\/$/, '') === b.replace(/\/$/, '');
  async function loadRanges(category, parameter) {
    if (!validURL(category)) throw Error('Missing collection');
    const requestURL = new URL(category.url.href);
    requestURL.search = '';
    requestURL.hash = '';
    requestURL.searchParams.set('section_id', 'bike-height-data');
    const cacheKey = requestURL.href;
    if (!rangeRequests.has(cacheKey)) {
      const task = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
          const response = await fetch(requestURL.href, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw Error('Height data request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const template = doc.querySelector('template[data-height-response]');
          if (!template || template.dataset.heightFilter !== parameter) throw Error('Missing height data section');
          const collectionURL = new URL(template.dataset.heightCollection, location.origin);
          if (!samePath(collectionURL.pathname, category.url.pathname)) throw Error('Unexpected collection response');
          const ranges = parseOptions(template);
          if (!ranges.length) throw Error('Missing height ranges');
          return ranges;
        } finally { clearTimeout(timeout); }
      })();
      rangeRequests.set(cacheKey, task);
      task.catch(() => { if (rangeRequests.get(cacheKey) === task) rangeRequests.delete(cacheKey); });
    }
    category.ranges = await rangeRequests.get(cacheKey);
    category.loaded = true;
  }
  async function warm(root) {
    const state = init(root), category = selectedCategory(root, state);
    if (!validURL(category) || category.loaded) return;
    // Warm only after a real form interaction; never during initial page load.
    try { await loadRanges(category, state.parameter); } catch (_) { /* Submit displays a retryable error. */ }
  }
  function matching(category, height) {
    return [...new Set(category.ranges.filter(range => height >= range.min && height <= range.max).map(range => range.value))];
  }
  function sync() {
    const query = new URL(location.href).searchParams;
    document.querySelectorAll('[data-bike-height-finder]').forEach(root => {
      const state = init(root);
      if (root.dataset.heightMode !== 'results') { refreshType(root); return; }
      const entry = [...state.categories].find(([, category]) => category.url && samePath(category.url.pathname, location.pathname));
      if (!entry) { root.hidden = true; return; }
      root.querySelectorAll('[data-height-type]').forEach(input => { input.checked = input.value === entry[0]; });
      const category = entry[1], height = readHeight(query.get(heightKey));
      const matches = height === null ? [] : matching(category, height);
      const selected = query.getAll(state.parameter).flatMap(value => value.split(','));
      const valid = matches.length > 0 && selected.length > 0 && selected.every(value => matches.includes(value));
      root.hidden = !valid;
      if (valid) {
        refreshType(root);
        root.querySelector('[data-height-input]').value = height;
        const label = root.querySelector('[data-height-selection]');
        label.textContent = `Fietsen voor jouw lengte: ${height} cm`;
        label.hidden = false;
      }
    });
  }
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-height-type]')) return;
    const root = event.target.closest('[data-bike-height-finder]');
    refreshType(root);
    warm(root);
  });
  document.addEventListener('focusin', event => {
    if (!event.target.matches('[data-height-input]')) return;
    warm(event.target.closest('[data-bike-height-finder]'));
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-height-form]');
    if (!form) return;
    event.preventDefault();
    const root = form.closest('[data-bike-height-finder]'), state = init(root);
    if (state.submitting) return;
    const category = selectedCategory(root, state);
    if (!validURL(category)) { refreshType(root); return; }
    const input = root.querySelector('[data-height-input]'), height = readHeight(input.value);
    if (height === null) {
      error(root, 'Vul je lengte in als een heel getal tussen 50 en 250 cm.', true);
      input.focus();
      return;
    }
    state.submitting = true;
    const button = root.querySelector('[data-height-submit]');
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    error(root, '');
    try {
      if (!category.loaded) await loadRanges(category, state.parameter);
      // User may change type or height while its ranges are loading.
      if (selectedCategory(root, state) !== category || readHeight(input.value) !== height) return;
      const matches = matching(category, height);
      if (!matches.length) {
        error(root, 'Geen passende lengterange gevonden voor dit type fiets. Kies een ander type of bekijk alle fietsen.');
        root.querySelector('[data-height-browse]').hidden = false;
        return;
      }
      const target = root.dataset.heightMode === 'results' ? new URL(location.href) : new URL(category.url.href);
      target.pathname = category.url.pathname;
      target.hash = '';
      target.searchParams.delete(state.parameter);
      target.searchParams.delete('page');
      target.searchParams.delete('section_id');
      matches.forEach(value => target.searchParams.append(state.parameter, value));
      target.searchParams.set(heightKey, String(height));
      if (root.dataset.heightMode !== 'results') target.searchParams.set('filter.v.availability', '1');
      location.assign(target.href);
    } catch (_) {
      if (selectedCategory(root, state) === category) {
        error(root, 'De lengtegegevens konden niet worden geladen. Probeer opnieuw of bekijk alle fietsen.');
        root.querySelector('[data-height-browse]').hidden = false;
      }
    } finally {
      state.submitting = false;
      form.removeAttribute('aria-busy');
      const active = selectedCategory(root, state);
      button.disabled = !validURL(active) || (active.loaded && !active.ranges.length);
    }
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-height-input]')) return;
    const root = event.target.closest('[data-bike-height-finder]');
    if (validURL(selectedCategory(root, init(root)))) { error(root, ''); warm(root); }
  });
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('filters-applied', sync);
  window.addEventListener('popstate', sync);
  window.addEventListener('pageshow', sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true });
  else sync();
})();
